import { useEffect, useRef, useState } from 'react';
import { TimerContainer } from '../../assets/styles/components/timerCard.styled';
import { Button, Text } from '../Ant';
import { v4 as uuidv4 } from 'uuid';

import { Pause, Play } from '../icons';
import { ITimer } from '../../interface/timer';
import { LOCAL_STORAGE_NAMES } from '../../constants/constants';
import { useTimerActions } from '../../hooks/useTimerActions';
import { computeInitialDuration, formatTime } from '../../constants/utilityFunctions';
import { IMatter } from '../../interface';
import { useUser } from '../../context/UserContext';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEmailCategoryTagger } from '../../hooks/useEmailCategoryTagger';

interface EntriesManagementTimerProps {
  subject: string;
  selectedMatter?: IMatter;
}
const EntriesManagementTimer = ({ subject, selectedMatter }: EntriesManagementTimerProps) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const { getItemId } = useEmailCategoryTagger();
  const timers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const currentOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
  const { organizationId } = currentOrganisation ? JSON.parse(currentOrganisation) : {};
  const [mailItemId, setMailItemId] = useState<string | null>(null);
  const currentTimer =
    timers.find(
      (t) =>
        t.itemId === mailItemId && t.organizationId === organizationId && t.userId === user?.id,
    ) ?? undefined;
  const [isRunning, setIsRunning] = useState<boolean>(currentTimer?.isRunning ?? false);
  const [seconds, setSeconds] = useState(computeInitialDuration(currentTimer?.id ?? '') ?? 0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [intervalStart, setIntervalStart] = useState<string | null>(
    currentTimer?.intervalStart ?? null,
  );
  const { toggleTimer } = useTimerActions({
    timer: currentTimer,
    timers,
    intervalStart,
    setIsRunning,
    setIntervalStart,
    setSeconds,
  });

  const focusPlayPauseRef = useRef(false);
  // Starting swaps the Start timer button for play/pause, so focus follows it there.
  const playPauseRef = (button: HTMLButtonElement | null) => {
    if (!button || !focusPlayPauseRef.current) return;
    focusPlayPauseRef.current = false;
    button.focus();
  };

  const handleTimer = () => {
    if (!isRunning && !currentTimer) {
      focusPlayPauseRef.current = true;
      let updatedTimers: ITimer[] = [];
      const newTimer = {
        subject: subject,
        matter: selectedMatter,
        id: uuidv4(),
        userId: user?.id,
        duration: 0,
        timerDate: dayjs(),
        itemId: mailItemId,
        organizationId,
      } as unknown as ITimer;
      updatedTimers = [...timers, newTimer];

      localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
      toggleTimer(newTimer);
    } else {
      toggleTimer();
    }
  };
  const fetchItemId = async () => {
    const item = Office.context.mailbox.item;
    const itemId = item?.itemId ?? (await getItemId(item));
    setMailItemId(itemId);
  };
  useEffect(() => {
    fetchItemId();
  }, [subject]);
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const baseDuration = currentTimer?.duration ?? 0;
        const start = currentTimer?.intervalStart
          ? new Date(currentTimer.intervalStart).getTime()
          : Date.now();
        const delta = Math.floor((now - start) / 1000);
        const total = baseDuration + delta;
        setSeconds(Math.max(total, 0));
      }, 1000);
    } else {
      setSeconds(currentTimer?.duration ?? 0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, intervalStart, currentTimer?.duration]);
  useEffect(() => {
    setIsRunning(currentTimer?.isRunning ?? false);
  }, [currentTimer?.isRunning]);
  return (
    <>
      {currentTimer ? (
        <TimerContainer>
          <Button
            ref={playPauseRef}
            data-testid="timer-play-pause-button"
            aria-label={t(isRunning ? 'common:timers.pauseTimer' : 'common:timers.startTimer')}
            type="text"
            icon={isRunning ? <Pause /> : <Play />}
            onClick={handleTimer}
          />
          <Text data-testid="timer-time-display" $fontsize="13px" $color="title" $weight={500}>
            {formatTime(seconds)}
          </Text>
        </TimerContainer>
      ) : (
        <Button onClick={handleTimer} type="text" className="text-cta">
          {t('common:timers.startTimer')}
        </Button>
      )}
    </>
  );
};

export default EntriesManagementTimer;
